import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { showApiError } from '@/lib/utils/notifications'
import { fetchEorpByDocumentId } from '@/lib/api/eorpService'
import { DocumentEorpModal } from '@/features/document-management/components/DocumentEorpModal'
import '@/features/document-management/styles/serviceAgreementHtml.css'

/**
 * Standalone EORP route — Angular `/explanation-service-aggrement/:id`.
 * Loads `/api/eorp/:id` and opens the explanation popup (same as Angular
 * `EorpDocumentComponent` → `EorpDocumentPopupComponent`). Inactive docs
 * redirect to login. 404s surface the API description via showApiError.
 */
export function EorpDocumentPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const numericId = Number(id)
  const [loading, setLoading] = useState(true)
  const [section, setSection] = useState('')
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    if (!numericId) return
    let cancelled = false
    /* eslint-disable react-hooks/set-state-in-effect -- resets fetch state when the document id changes */
    setLoading(true)
    setSection('')
    setModalOpen(false)
    /* eslint-enable react-hooks/set-state-in-effect */

    void fetchEorpByDocumentId(numericId)
      .then((doc) => {
        if (cancelled) return
        const active =
          doc.is_active === true ||
          doc.is_active === 1 ||
          doc.is_active === '1' ||
          String(doc.is_active).toLowerCase() === 'true'
        if (!active) {
          navigate('/login', { replace: true })
          return
        }
        setSection(doc.eorp_section ?? '')
        setModalOpen(true)
      })
      .catch((error) => {
        if (cancelled) return
        // Angular: `notify(error.error, 0)` — keep the API 404/body description.
        showApiError(error)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [numericId, navigate])

  const handleClose = () => {
    setModalOpen(false)
    // Angular popup sits on an empty route shell; closing returns to dashboard.
    if (window.history.length > 1) navigate(-1)
    else navigate('/dashboard', { replace: true })
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="eorp-document-page">
      <div className="mx-auto max-w-4xl rounded bg-white p-6 shadow">
        <h1 className="mb-4 text-xl font-semibold text-slate-900">
          Explanation of Reimbursement Process
        </h1>
        {loading ? (
          <div className="text-sm text-slate-500" data-testid="eorp-loading">
            Loading document…
          </div>
        ) : null}
      </div>

      <DocumentEorpModal
        isOpen={modalOpen && Boolean(numericId)}
        documentId={numericId || null}
        initialHtml={section}
        onClose={handleClose}
      />
    </div>
  )
}
